import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { Store } from '../../store/store';
import { StoreNode } from '../../store';
import { JobCatalogSource, JobModel } from '../../entities';
import { WindowState } from '../overlays/windowState';
import { input, InputState } from '../input';
import { notifyError } from '../../services/notifications';

const GET_CUSTOM_SPECIALTIES = `
  query GetCustomSpecialties($teamId: ID!, $status: CustomSpecialtyStatus) {
    getCustomSpecialties(teamId: $teamId, status: $status) { id name }
  }
`;

const CREATE_COMBINED_REPORT = `
  mutation CreateCombinedReport($input: CreateCombinedReportInput!) {
    createCombinedReport(input: $input) { id }
  }
`;

export class CombinedReportDrawerState extends StoreNode {
  constructor(store: Store, private readonly source: JobCatalogSource) {
    super(store);
    makeObservable(this, {
      formats: observable,
      formatId: observable,
      isSubmitting: observable,
      sources: computed,
      canSubmit: computed,
      open: action,
      close: action,
      setFormat: action,
      submit: action,
    });
    this.directions = input(this, {
      name: 'directions',
      isRequired: true,
      multiline: true,
      placeholder: 'How should the report be structured? For example: a timeline first, then a summary of each witness.',
    });
  }

  readonly window = new WindowState(this.store);
  readonly directions: InputState;
  formats: Array<{ id: string; name: string }> = [];
  formatId = '';
  isSubmitting = false;
  private teamId: string | null = null;

  get sources(): JobModel[] {
    return this.source.selectedJobs;
  }

  get canSubmit(): boolean {
    return this.sources.length > 0 && !!this.directions.value?.trim() && !this.isSubmitting;
  }

  async open(teamId: string) {
    this.teamId = teamId;
    this.window.open();
    const [res] = await this.store.apiService.runRequest<{ getCustomSpecialties?: Array<{ id: string; name: string }> }>(
      GET_CUSTOM_SPECIALTIES, { teamId, status: 'Active' });
    runInAction(() => { this.formats = res?.getCustomSpecialties ?? []; });
  }

  close() {
    this.window.close();
  }

  remove(job: JobModel) {
    this.source.toggleSelection(job);
  }

  setFormat(formatId: string) {
    this.formatId = formatId;
  }

  async submit() {
    if (!this.canSubmit || !this.teamId) return;
    this.isSubmitting = true;
    const [, err] = await this.store.apiService.runRequest(CREATE_COMBINED_REPORT, {
      input: {
        teamId: this.teamId,
        sourceJobIds: this.sources.map(job => job.id),
        directions: this.directions.value!.trim(),
        customSpecialtyId: this.formatId || undefined,
        folderId: this.source.folderId ?? undefined,
      },
    });
    runInAction(() => { this.isSubmitting = false; });
    if (err) {
      notifyError(this, err.message || 'Could not create the combined report.');
      return;
    }
    this.close();
    this.directions.clear();
    this.setFormat('');
    this.source.clearSelection();
    void this.source.fetch();
  }
}
